import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase.js';
import { IS_DEMO } from './config.js';
import { DEMO_USER, DEMO_EMP_BY_ROLE } from './demo.js';

// Auth context carries the Supabase session plus the hr.app_users row
// (role + employee_id + branch_scope) so route guards can gate by role.
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [appUser, setAppUser] = useState(null); // { id, role, employee_id, branch_scope }
  const [loading, setLoading] = useState(true);
  // DEMO: which role to preview (managers trial first → default manager)
  const [demoRole, setDemoRole] = useState(() => localStorage.getItem('hr-demo-role') || 'manager');

  // DEMO mode: sign in as a fake user of the chosen role (switchable, no Supabase)
  useEffect(() => {
    if (!IS_DEMO) return;
    setSession({ user: { id: DEMO_USER.id, email: 'demo@campstudio.app' } });
    setAppUser({ ...DEMO_USER, role: demoRole, employee_id: DEMO_EMP_BY_ROLE[demoRole] || 'e2' });
    setLoading(false);
  }, [demoRole]);

  function changeDemoRole(r) {
    localStorage.setItem('hr-demo-role', r);
    setDemoRole(r);
  }

  useEffect(() => {
    if (IS_DEMO) return;

    let active = true;

    async function loadAppUser(sess) {
      if (!sess) { setAppUser(null); return; }
      // RLS policy p_app_users_self lets a user read their own row.
      const { data, error } = await supabase
        .from('app_users')
        .select('id, role, employee_id, branch_scope')
        .eq('id', sess.user.id)
        .maybeSingle();
      if (!active) return;
      if (error) console.error('[auth] app_users load failed:', error.message);
      setAppUser(data ?? null);
    }

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      await loadAppUser(data.session);
      if (active) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_e, sess) => {
      setSession(sess);
      loadAppUser(sess);
    });

    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  const value = {
    session,
    appUser,
    role: appUser?.role ?? null,
    employeeId: appUser?.employee_id ?? null,
    loading,
    isDemo: IS_DEMO,
    demoRole,
    changeDemoRole,
    signOut: () => (IS_DEMO ? Promise.resolve() : supabase.auth.signOut()),
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
